import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import BusinessRegistrationRouteGuard from './components/BusinessRegistrationRouteGuard';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import HomePage from './pages/HomePage';
import CategoriesPage from './pages/CategoriesPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import BusinessesListPage from './pages/BusinessesListPage';
import BusinessDetailPage from './pages/BusinessDetailPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminBusinessesPage from './pages/admin/AdminBusinessesPage';
import AdminConsumersPage from './pages/admin/AdminConsumersPage';
import AdminModerationPage from './pages/admin/AdminModerationPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminBusinessReviewsPage from './pages/admin/AdminBusinessReviewsPage';
import AdminBusinessDetailPage from './pages/admin/AdminBusinessDetailPage';
import BusinessRegistrationPage from './pages/BusinessRegistrationPage';
import BusinessRegistrationUnavailablePage from './pages/BusinessRegistrationUnavailablePage';
import ConsumerDashboard from './pages/consumer/ConsumerDashboard';
import ConsumerProfilePage from './pages/consumer/ConsumerProfilePage';
import ConsumerReviewsPage from './pages/consumer/ConsumerReviewsPage';
import BusinessDashboard from './pages/business/BusinessDashboard';
import BusinessReviewsPage from './pages/business/BusinessReviewsPage';
import BusinessProfilePage from './pages/business/BusinessProfilePage';
import BusinessSettingsPage from './pages/business/BusinessSettingsPage';
import BusinessHelpPage from './pages/business/BusinessHelpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <Router>
      <AuthProvider>
        <Routes>
          {/* Admin Routes - No Navbar/Footer */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          
          {/* Admin Dashboard Pages */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminCategoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/businesses"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminBusinessesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/businesses/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminBusinessDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/businesses/:id/reviews"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminBusinessReviewsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consumers"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminConsumersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/moderation"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminModerationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminSettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Consumer Routes - No Navbar/Footer */}
          <Route
            path="/consumer/dashboard"
            element={
              <ProtectedRoute requiredRole="consumer">
                <ConsumerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consumer/profile"
            element={
              <ProtectedRoute requiredRole="consumer">
                <ConsumerProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consumer/reviews"
            element={
              <ProtectedRoute requiredRole="consumer">
                <ConsumerReviewsPage />
              </ProtectedRoute>
            }
          />

          {/* Business Routes - No Navbar/Footer */}
          <Route
            path="/business/dashboard"
            element={
              <ProtectedRoute requiredRole="business_owner">
                <BusinessDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/reviews"
            element={
              <ProtectedRoute requiredRole="business_owner">
                <BusinessReviewsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/profile"
            element={
              <ProtectedRoute requiredRole="business_owner">
                <BusinessProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/settings"
            element={
              <ProtectedRoute requiredRole="business_owner">
                <BusinessSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business/help"
            element={
              <ProtectedRoute requiredRole="business_owner">
                <BusinessHelpPage />
              </ProtectedRoute>
            }
          />

          {/* Public Routes with Navbar/Footer */}
          <Route
            path="/*"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50">
                <Navbar />
                <main className="flex-grow">
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/categories" element={<CategoriesPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route
                      path="/register-business"
                      element={
                        <BusinessRegistrationRouteGuard>
                          <BusinessRegistrationPage />
                        </BusinessRegistrationRouteGuard>
                      }
                    />
                    <Route
                      path="/register-business-unavailable"
                      element={<BusinessRegistrationUnavailablePage />}
                    />
                    <Route path="/businesses" element={<BusinessesListPage />} />
                    <Route path="/bisnesses" element={<BusinessesListPage />} />
                    <Route path="/businesses/:id" element={<BusinessDetailPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            }
          />
        </Routes>
      </AuthProvider>
    </Router>
    </QueryClientProvider>
  );
}

export default App;
